export const SERVICE_PRICE_MIN_KEY = "service_price_min";
export const SERVICE_PRICE_MAX_KEY = "service_price_max";

/** Absolute bounds a configured limit must sit inside; mirrors the service schema. */
export const LIMIT_FLOOR = 0.01;
export const LIMIT_CEILING = 100000;

export interface ServicePriceLimits {
  min: number | null;
  max: number | null;
}

export const NO_LIMITS: ServicePriceLimits = { min: null, max: null };

/** Reads a stored setting value. Empty, missing or unparseable means "no limit". */
export function parseLimit(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed.replace(",", "."));
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export function formatLimit(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/**
 * Checks a service price WITHOUT VAT (platformBase, the number the therapist types)
 * against the configured range. The range is never applied to the gross client
 * price: callers holding a stored gross convert it first (`basePriceFromClientPrice`).
 * Returns a Bosnian error message, or null when the price is allowed.
 */
export function checkServicePrice(price: number, limits: ServicePriceLimits): string | null {
  const { min, max } = limits;
  const belowMin = min !== null && price < min;
  const aboveMax = max !== null && price > max;
  if (!belowMin && !aboveMax) return null;

  if (min !== null && max !== null) {
    return `Cijena bez PDV-a mora biti između ${formatLimit(min)} i ${formatLimit(max)} KM`;
  }
  if (belowMin && min !== null) {
    return `Cijena bez PDV-a ne može biti niža od ${formatLimit(min)} KM`;
  }
  return `Cijena bez PDV-a ne može biti viša od ${formatLimit(max as number)} KM`;
}

/** Helper line shown under the price field, or null when no range is configured. */
export function describeLimits(limits: ServicePriceLimits): string | null {
  const { min, max } = limits;
  if (min !== null && max !== null) {
    return `Dozvoljena cijena bez PDV-a: ${formatLimit(min)} do ${formatLimit(max)} KM`;
  }
  if (min !== null) return `Najniža dozvoljena cijena bez PDV-a: ${formatLimit(min)} KM`;
  if (max !== null) return `Najviša dozvoljena cijena bez PDV-a: ${formatLimit(max)} KM`;
  return null;
}
